import React, { useEffect, useState } from 'react';
import { X, Calendar, Download, Eye, Filter, Loader2, Columns, FileSpreadsheet } from 'lucide-react';

/**
 * Reusable Report dialog with filters, column picker, preview popup, CSV download.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string                        // e.g. "Download jobs report"
 *  - columns: [{key, label}]              // available columns
 *  - defaultSelected: string[]            // pre-checked column keys
 *  - filterSchema: [{key, label, type, options?}]  // type: 'date' | 'text' | 'number' | 'select'
 *  - defaultFilters: {key: value}
 *  - fetchPreview: (filters, selectedCols) => Promise<{columns, rows, total, shown, preview_limit}>
 *  - downloadCsv:  (filters, selectedCols) => void  // triggers browser download via auth+blob
 *  - filenameHint?: string
 */
export default function ReportDialog({
  open, onClose, title, columns, defaultSelected = [], filterSchema = [], defaultFilters = {},
  fetchPreview, downloadCsv, filenameHint = 'report.csv',
}) {
  const [filters, setFilters] = useState(defaultFilters);
  const [selected, setSelected] = useState(defaultSelected.length ? defaultSelected : columns.map(c => c.key));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFilters(defaultFilters);
      setSelected(defaultSelected.length ? defaultSelected : columns.map(c => c.key));
      setPreview(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    // If columns prop changes (different group_by), reset selection
    setSelected(defaultSelected.length ? defaultSelected : columns.map(c => c.key));
    setPreview(null);
  }, [columns]);

  if (!open) return null;

  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const toggleColumn = (k) => setSelected(prev => prev.includes(k) ? prev.filter(c => c !== k) : [...prev, k]);
  const allOn = () => setSelected(columns.map(c => c.key));
  const allOff = () => setSelected([]);

  const onPreview = async () => {
    setError(null);
    if (!selected.length) { setError('Pick at least one column'); return; }
    setLoading(true);
    try {
      const r = await fetchPreview(filters, selected);
      setPreview(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  const onDownload = async () => {
    setError(null);
    if (!selected.length) { setError('Pick at least one column'); return; }
    setDownloading(true);
    try {
      await downloadCsv(filters, selected);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setDownloading(false);
  };

  // PREVIEW MODE
  if (preview) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="report-preview-modal">
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-5xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-zinc-800">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Report preview</h2>
              <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1" data-testid="preview-summary">
                Showing <span className="text-slate-900 dark:text-white">{preview.shown}</span> of <span className="text-slate-900 dark:text-white">{preview.total}</span> rows
                {preview.total > preview.preview_limit && <> · Full CSV will include all {preview.total} rows</>}
                {' '}· {preview.columns.length} column{preview.columns.length !== 1 && 's'}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-preview-btn"><X className="w-5 h-5" /></button>
          </div>

          {error && <div className="m-4 px-3 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-sm">{error}</div>}

          <div className="overflow-auto flex-1" data-testid="preview-table-wrap">
            {preview.rows.length === 0 ? (
              <div className="text-slate-500 dark:text-zinc-500 text-sm p-10 text-center">No rows match these filters.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 dark:bg-[#0a0a0b] border-b border-slate-200 dark:border-zinc-800/70">
                  <tr>
                    {preview.columns.map(c => (
                      <th key={c.key} className="text-left text-slate-500 dark:text-zinc-400 font-medium px-3 py-2 whitespace-nowrap">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => (
                    <tr key={i} className="border-b border-slate-200 dark:border-zinc-900/60 hover:bg-slate-50 dark:hover:bg-[#0a0a0b]" data-testid={`preview-row-${i}`}>
                      {preview.columns.map(c => (
                        <td key={c.key} className="text-slate-600 dark:text-zinc-300 px-3 py-2 whitespace-nowrap max-w-[260px] truncate" title={String(row[c.key] ?? '')}>{String(row[c.key] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 p-4 border-t border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-[#0a0a0b]/60">
            <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-zinc-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-zinc-800/80 hover:bg-slate-100 dark:hover:bg-zinc-800" data-testid="back-to-filters-btn">← Back to filters</button>
            <button onClick={onDownload} disabled={downloading} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-zinc-100 disabled:opacity-50" data-testid="confirm-download-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? 'Preparing…' : `Confirm & download CSV (${preview.total})`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // FILTER MODE
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="report-dialog">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-zinc-800">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> {title}</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">Pick filters and columns. Preview before download.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="close-report-dialog"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-auto flex-1 p-5 space-y-5">
          {/* Filters */}
          <section data-testid="report-filters">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 dark:text-zinc-400 mb-2 flex items-center gap-1.5"><Filter className="w-3 h-3" /> Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filterSchema.map(f => (
                <div key={f.key}>
                  <label className="text-[11px] text-slate-500 dark:text-zinc-500 mb-1 block">{f.label}</label>
                  {f.type === 'date' ? (
                    <div className="relative">
                      <Calendar className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-500 dark:text-zinc-500" />
                      <input type="date" value={filters[f.key] || ''} onChange={e => updateFilter(f.key, e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid={`filter-${f.key}`} />
                    </div>
                  ) : f.type === 'select' ? (
                    <select value={filters[f.key] || ''} onChange={e => updateFilter(f.key, e.target.value)} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid={`filter-${f.key}`}>
                      <option value="">All</option>
                      {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input type={f.type === 'number' ? 'number' : 'text'} value={filters[f.key] ?? ''} onChange={e => updateFilter(f.key, e.target.value)} placeholder={f.placeholder || ''} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid={`filter-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Columns */}
          <section data-testid="report-columns">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 dark:text-zinc-400 flex items-center gap-1.5"><Columns className="w-3 h-3" /> Columns to include · {selected.length}/{columns.length}</h3>
              <div className="flex gap-2 text-xs">
                <button onClick={allOn} className="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="cols-all-on">All</button>
                <span className="text-slate-600 dark:text-zinc-700">|</span>
                <button onClick={allOff} className="text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="cols-all-off">None</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-auto pr-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/70 rounded-lg p-2">
              {columns.map(c => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-zinc-800/60 cursor-pointer text-xs text-slate-600 dark:text-zinc-300" data-testid={`col-${c.key}`}>
                  <input type="checkbox" checked={selected.includes(c.key)} onChange={() => toggleColumn(c.key)} className="accent-emerald-500" />
                  <span className="truncate">{c.label}</span>
                </label>
              ))}
            </div>
          </section>

          {error && <div className="px-3 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-sm" data-testid="report-error">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-[#0a0a0b]/60">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-zinc-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-zinc-800/80 hover:bg-slate-100 dark:hover:bg-zinc-800" data-testid="cancel-report-btn">Cancel</button>
          <button onClick={onPreview} disabled={loading || !selected.length} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-zinc-100 disabled:opacity-50" data-testid="preview-report-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            {loading ? 'Loading…' : 'Preview'}
          </button>
        </div>
      </div>
    </div>
  );
}
